import * as React from 'react'
import {FC, useCallback, useEffect, useState} from 'react'
import {observer} from 'mobx-react'
import {Box, makeStyles} from '@material-ui/core'
import {Layout, Layouts, Responsive, WidthProvider} from 'react-grid-layout'

import FormattingService from '../Services/FormattingService'
import {SSMPalette} from './AppTheme'
import {Widget, WidgetItem} from './Widgets/Widget'
import {RGL_RESTORE_LAYOUT, RGL_WIDGET_RESIZE} from '../AppConstants'
import {appStore} from '../index'
import {LocalStorageGet, LocalStorageSet} from '../Services/Helpers'
import DateTimeService from '../Services/DateTimeService';

const ResponsiveReactGridLayout = WidthProvider(Responsive)

type GridLayoutProps = {
    widgetsGroup: string,
    refreshTime?: Date,
    widgets?: WidgetItem[]
    filters?: JSX.Element
}

type ResetLayoutProps = {
    refreshTime?: Date
}

const useStyles = makeStyles((theme) => ({
    reset: {
        position: 'absolute',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        right: 0,
        top: 0,
        fontSize: 0,
        lineHeight: '26px',
        padding: theme.spacing(1, 3, 0),
        textAlign: 'center',
        zIndex: 5,
        '& svg': {
            cursor: 'pointer',
        },
    },
    controls: {
        display: 'flex',
        padding: theme.spacing(0, 1.5),
        alignItems: 'center',
    },
    dateReset: {
        marginLeft: 20,
        marginRight: -theme.spacing(3),
    },
    time: {
        marginLeft: 'auto',
        color: SSMPalette.gray.graphite,
        textAlign: 'right',
        fontWeight: 400,
        fontSize: 14,
        paddingLeft: theme.spacing(2),

    },
}))

const ResetLayout: FC<ResetLayoutProps> = ({refreshTime}) => {
    const classes = useStyles()

    const dateString = refreshTime ?
        FormattingService.formatDate(
            refreshTime,
            {
                month: 'long',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
            },
        ) :
        ''

    return (
        <div className={classes.reset}>
            <div className={classes.controls}>
                <div className={classes.time}>{refreshTime ? DateTimeService.format(refreshTime, 'DD. MMM HH:mm'): ''}</div>
            </div>
        </div>
    )
}

const RGL_Prefix = `rgl_`
export const GridLayout: FC<GridLayoutProps> = observer(
    ({refreshTime, widgetsGroup, widgets, filters}) => {

        const _getFromLS = (key: string): Layouts | null => {
            try {
                const val = LocalStorageGet<string>(`${RGL_Prefix}${key}`)
                return val ? JSON.parse(val)[key] : null
            } catch (e) {
                return null
            }
        }

        const _saveToLS = (key: string, value: Layouts) => {
            const keyStr = `${RGL_Prefix}${key}`
            let valueStr = JSON.stringify({[key]: value})
            LocalStorageSet(keyStr, valueStr)
        }

        const _updateContainerSizes = () => {
            document.dispatchEvent(new Event(RGL_WIDGET_RESIZE))
        }

        const [layouts, setLayouts] = useState<Layouts>(_getFromLS(widgetsGroup) || {})

        const _updateLayouts = useCallback((layouts: Layouts) => {
            setLayouts(layouts)
            _updateContainerSizes()
        }, [])

        const _restoreLayout = useCallback(() => {
            const layouts = {}
            const keyStr = `${RGL_Prefix}${widgetsGroup}`
            global.localStorage && delete global.localStorage[keyStr]
            _saveToLS(widgetsGroup, layouts)
            _updateLayouts(layouts)
        }, [widgetsGroup, _updateLayouts])

        useEffect(() => {
            document.addEventListener(RGL_RESTORE_LAYOUT, _restoreLayout)
            return () => {
                document.removeEventListener(RGL_RESTORE_LAYOUT, _restoreLayout)
            }
        }, [_restoreLayout])

        useEffect(() => {
            appStore.showGridReset()
            return () => {
                appStore.hideGridReset()
            }
        }, [widgetsGroup])

        const _onLayoutChange = (currentLayout: Layout[], layouts: Layouts) => {
            _saveToLS(widgetsGroup, layouts)
            _updateLayouts(layouts)
        }

        return (
            <Box padding={3}>
                <ResetLayout
                    refreshTime={refreshTime}
                />
                {!!filters && filters}
                <ResponsiveReactGridLayout
                    layouts={layouts}
                    isBounded={true}
                    onLayoutChange={_onLayoutChange}
                    onWidthChange={_updateContainerSizes}
                    draggableCancel=".js-draggableCancel"
                    draggableHandle=".js-draggableHandle"
                    cols={{lg: 6, md: 6, sm: 6, xs: 4, xxs: 2}}
                    rowHeight={15}
                    containerPadding={[10, 10]}
                    margin={[20, 20]}
                >
                    {widgets?.map(el => {
                        return (
                            <div key={el.gridItem.i} data-grid={el.gridItem} style={el.customStyles}>
                                <Widget
                                    label={el.label}
                                    bodySpacing={el.bodySpacing}
                                    isPrimaryHeaderStyle={el.isPrimaryHeaderStyle}
                                    headerClassName={el.headerClassName}
                                >
                                    {widgetSize => el.node(widgetSize)}
                                </Widget>
                            </div>
                        )
                    })}
                </ResponsiveReactGridLayout>
            </Box>
        )
    },
)
